import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Link, Navigate, useLocation, unstable_usePrompt as usePrompt } from 'react-router-dom'

import SearchButton from 'components/form/SearchButton'
import CourseBasicData from 'components/form/course/CourseBasicData'
import CourseVersions from 'components/form/course/CourseVersions'
import CourseHoles from 'components/form/course/CourseHoles'
import CourseTeeBoxes from 'components/form/course/CourseTeeBoxes'

import ErrorMessage from 'components/ui/ErrorMessage'

import { ClientRoute } from 'routes/client'
import { selectCourse } from 'store/course/slice'
import { createCourseVersion } from 'store/course/thunks'
import { compareData } from 'utils/compareData'
import { COURSE_DATA_SOURCE } from 'config'
import { validateCourseHoles, validateCourseTeeBoxes } from 'store/course/utils'
import useUserRole from 'hooks/useUserRole'

interface Props {
  course: Course
  clubId: number | null
  toggleClubModal: () => void
}

export default function CourseForm({ course, clubId, toggleClubModal }: Props): JSX.Element {
  const [selectedCourseVersion, setSelectedCourseVersion] = useState<CourseVersion | null>(null)
  const [courseVersionValidationFailed, setCourseVersionValidationFailed] = useState<boolean>(false)
  const [holeValidationErrors, setHoleValidationErrors] = useState<string[]>([])
  const [teeBoxValidationErrors, setTeeBoxValidationErrors] = useState<string[]>([])
  const [activeHoles, setActiveHoles] = useState<CourseHole[]>([])
  const [activeTeeBoxes, setActiveTeeBoxes] = useState<CourseTeeBox[]>([])
  const [creatingNewVersion, setCreatingNewVersion] = useState<boolean>(false)
  const { isAdmin, isAdminCourseEditor } = useUserRole()

  const { versionSaveStatus, initialMappingStatus } = useSelector(selectCourse)

  const intl = useIntl()
  const dispatch = useDispatch()
  const location = useLocation()

  const latestVersion = useMemo(() => {
    if (course && course.versions) {
      const versions = course.versions
        .slice()
        .sort((a: CourseVersion, b: CourseVersion) => b.version - a.version)
        .splice(0, 1)

      return versions[0]
    }
    return null
  }, [course])

  const officialVersion = useMemo(() => {
    if (course && course.versions) {
      const versions = course.versions.filter((cv: CourseVersion) => cv.isOfficial)

      return versions.length > 0 ? versions[0] : null
    }
    return null
  }, [course])

  const sortedHoles = useMemo(() => {
    return (
      selectedCourseVersion?.holes
        ?.slice()
        .sort((a: CourseHole, b: CourseHole) => (a.holeNumber > b.holeNumber ? 1 : -1)) || []
    )
  }, [selectedCourseVersion])

  const sortedTeeBoxes = useMemo(() => {
    return (
      selectedCourseVersion?.teeBoxes?.slice().sort((a: CourseTeeBox, b: CourseTeeBox) => (a.id > b.id ? 1 : -1)) || []
    )
  }, [selectedCourseVersion])

  const dataModified: boolean = useMemo(() => {
    const holesChanged = compareData(sortedHoles, activeHoles)
    const teeBoxesChanged = compareData(sortedTeeBoxes, activeTeeBoxes)
    const hasVersions =
      course?.versionsCount !== undefined && course?.versionsCount !== null && course?.versionsCount > 0
    return hasVersions && (holesChanged || teeBoxesChanged)
  }, [course, activeHoles, activeTeeBoxes, sortedHoles, sortedTeeBoxes])

  useEffect(() => {
    const newVersionCreated =
      creatingNewVersion && versionSaveStatus === 'succeeded' && latestVersion?.id !== selectedCourseVersion?.id
    if (latestVersion && (!selectedCourseVersion || newVersionCreated)) {
      const newVersion = creatingNewVersion || !officialVersion ? latestVersion : officialVersion
      setSelectedCourseVersion(newVersion)
      setCreatingNewVersion(false)
    }
  }, [latestVersion, officialVersion, selectedCourseVersion, creatingNewVersion, versionSaveStatus])

  const changeSelectedVersion = (cv: CourseVersion) => {
    const confirmMessage = intl.formatMessage({ id: 'strings.confirmCourseVersionChange' })
    if (!dataModified || (dataModified && window.confirm(confirmMessage))) {
      setSelectedCourseVersion(cv)
    }
  }

  const leavePageConfirmationMessage = () => {
    return intl.formatMessage({ id: 'strings.leavePageConfirmation' })
  }

  useEffect(() => {
    function confirmLeaving(event: any) {
      if (dataModified) {
        event.preventDefault()
        return (event.returnValue = leavePageConfirmationMessage())
      }
      return
    }
    window.addEventListener('beforeunload', confirmLeaving)

    return () => {
      window.removeEventListener('beforeunload', confirmLeaving)
    }
  })

  usePrompt({ when: dataModified, message: leavePageConfirmationMessage() })

  const validateHoles = (holes: CourseHole[] = activeHoles) => {
    const { holeValidationErrorsArray, noDuplicates } = validateCourseHoles(holes, intl)

    const isValid = holeValidationErrorsArray.length === 0 && noDuplicates
    setHoleValidationErrors(holeValidationErrorsArray)
    setCourseVersionValidationFailed(!isValid)
    return isValid
  }

  const validateTeeBoxes = (teeBoxes: CourseTeeBox[] = activeTeeBoxes) => {
    const { teeBoxValidationErrorsArray } = validateCourseTeeBoxes(teeBoxes, intl)

    setTeeBoxValidationErrors(teeBoxValidationErrorsArray)
    return teeBoxValidationErrorsArray.length === 0
  }

  const syncHoles = (holes: CourseHole[]) => {
    if (courseVersionValidationFailed) {
      validateHoles(holes)
    }
    setActiveHoles(holes)
  }

  const syncTeeBoxes = (teeBoxes: CourseTeeBox[]) => {
    setActiveTeeBoxes(teeBoxes)
  }

  const submitNewCourseVersion = () => {
    setHoleValidationErrors([])
    const validHoles = validateHoles()
    const validTeeBoxes = validateTeeBoxes()
    if (validHoles && validTeeBoxes) {
      const payload: CourseVersionPayload = {
        courseId: course.id,
        successMessage: intl.formatMessage({ id: 'strings.courseVersionSavedSuccessfully' }),
        data: {
          holes: activeHoles.slice().sort((a, b) => (a.holeNumber > b.holeNumber ? 1 : -1)),
          teeBoxes: activeTeeBoxes,
          source: isAdmin || isAdminCourseEditor ? COURSE_DATA_SOURCE.GGB_ADMIN : COURSE_DATA_SOURCE.GGB_USER,
        },
      }
      dispatch(createCourseVersion(payload))
      setCreatingNewVersion(true)
    }
  }

  const isCourseDataModifiable = () => {
    return course.provider?.options?.modificationAllowed === true || !course.provider
  }

  const isNewCourseVersionButtonDisabled = () => {
    return (courseVersionValidationFailed || !isCourseDataModifiable()) && course.versions && course.versions.length > 0
  }

  if (
    course.id > 0 &&
    clubId &&
    initialMappingStatus === 'succeeded' &&
    location.pathname === ClientRoute.ADMIN_ADD_NEW_CLUB_COURSE(clubId)
  ) {
    return <Navigate to={ClientRoute.ADMIN_CLUB_COURSE(clubId, course.id)} />
  }

  return (
    <PageContainer>
      <SectionContainer>
        <CourseBasicData course={course} clubId={clubId} />
      </SectionContainer>
      {course.id > 0 && (
        <>
          <SectionContainer>
            <CourseVersions
              course={course}
              version={selectedCourseVersion}
              changeSelectedVersion={changeSelectedVersion}
            />
          </SectionContainer>
          <SectionContainer>
            <CourseHoles
              course={course}
              version={selectedCourseVersion}
              syncHoles={syncHoles}
              isModifiable={isCourseDataModifiable()}
            />
            {holeValidationErrors.length > 0 && (
              <ErrorMessage>
                <ul>
                  {holeValidationErrors.map((error) => (
                    <li>{error}</li>
                  ))}
                </ul>
              </ErrorMessage>
            )}
          </SectionContainer>
          <SectionContainer>
            <CourseTeeBoxes
              version={selectedCourseVersion}
              syncTeeBoxes={syncTeeBoxes}
              isModifiable={isCourseDataModifiable()}
            />
            {teeBoxValidationErrors.length > 0 && (
              <ErrorMessage>
                <ul>
                  {teeBoxValidationErrors.map((error) => (
                    <li>{error}</li>
                  ))}
                </ul>
              </ErrorMessage>
            )}
          </SectionContainer>
          <SectionContainer>
            <CourseActionButtons>
              <CreateCourseVersion
                onClick={submitNewCourseVersion}
                text={intl.formatMessage({ id: 'strings.createCourseVersion' })}
                status={versionSaveStatus}
                disabled={isNewCourseVersionButtonDisabled()}
              />
              <ActionButton onClick={toggleClubModal}>{intl.formatMessage({ id: 'strings.editClub' })}</ActionButton>
              {course.club?.id && (
                <LinkButton to={ClientRoute.ADMIN_CLUB_COURSES(course.club.id)}>
                  {intl.formatMessage({ id: 'strings.backToCourses' })}
                </LinkButton>
              )}
            </CourseActionButtons>
          </SectionContainer>
        </>
      )}
    </PageContainer>
  )
}

const PageContainer = styled.div`
  margin: 0 10px;
  padding: 0 0 20px 0;
  min-height: 500px;

  & a {
    color: ${(props) => props.theme.palette.primary.main};
    text-decoration: none;
  }
`

const SectionContainer = styled.div`
  background-color: ${(props) => props.theme.customPalette.light};
  margin: 0 0 10px;
  padding: 20px;
`

const CourseActionButtons = styled.div`
  display: flex;
  & > * {
    margin-right: 30px;
  }
`

const CreateCourseVersion = styled(SearchButton)``

const LinkButton = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 180px;
  height: 50px;
  color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  border-radius: 5px;
  font-weight: 500;
  font-size: 0.9em;
  text-decoration: none;

  &:hover {
    opacity: 0.85;
  }
`

const ActionButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 180px;
  height: 54px;
  background-color: transparent;
  color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  border-radius: 5px;
  font-size: 0.9em;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`
